import { useEffect } from 'react'
import { useStripeCheckoutContext } from '~/contexts/StripeCheckoutContext'

export type { CheckoutStatus } from '~/contexts/StripeCheckoutContext'

interface UseStripeCheckoutOptions {
  billingId: string | number | undefined
  onSuccess: () => void
  onFailure: () => void
  /** When true, register as the active checkout consumer. Only the open dialog should set this. */
  isActive?: boolean
}

interface UseStripeCheckoutReturn {
  isLoading: boolean
  checkoutStatus: ReturnType<typeof useStripeCheckoutContext>['checkoutStatus']
  startCheckout: (getRedirectUrl: () => Promise<string>) => Promise<boolean>
  cleanup: () => void
}

/**
 * Registers with the shared StripeCheckoutContext and returns checkout controls.
 * Consumers (PlanDialog, ZeroCreditsDialog, ManageAccountDialog) share a single
 * checkout instance — no duplicate listeners or toasts.
 * Set isActive=true only when your dialog is open, so the correct callbacks are used.
 */
export function useStripeCheckout({
  billingId,
  onSuccess,
  onFailure,
  isActive = true
}: UseStripeCheckoutOptions): UseStripeCheckoutReturn {
  const { isLoading, checkoutStatus, startCheckout, cleanup, register } = useStripeCheckoutContext()

  useEffect(() => {
    if (isActive) {
      register({ billingId, onSuccess, onFailure })
    } else {
      register(null)
    }
    return () => register(null)
  }, [register, isActive, billingId, onSuccess, onFailure])

  return { isLoading, checkoutStatus, startCheckout, cleanup }
}
